import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LegacyColumnDef as ColumnDef } from '@tanstack/react-table/legacy'
import { Copy, Plus } from 'lucide-react'
import { useState } from 'react'

import { PagedDataTable } from '@/components/data-table/PagedDataTable'
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { MultiSelect } from '@/components/filters/MultiSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { UserDetailPanel } from '@/features/admin/UserDetailPanel'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import type { User } from '@/types/models'

const PAGE_SIZE = 20

function TempPasswordDialog({
  info,
  onClose,
}: {
  info: { email: string; password: string } | null
  onClose: () => void
}) {
  return (
    <Dialog open={info !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.users.tempPasswordTitle}</DialogTitle>
          <DialogDescription>{messages.users.tempPasswordHint}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {info && (
            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground text-sm">{info.email}</p>
              <div className="flex items-center gap-2">
                <code className="border-border bg-muted tabular flex-1 rounded-sm border px-3 py-2 text-sm">
                  {info.password}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => void navigator.clipboard.writeText(info.password)}
                  aria-label={messages.common.copy}
                >
                  <Copy />
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>{messages.common.close}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreateUserDialog({
  open,
  onOpenChange,
  groupOptions,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupOptions: { value: string; label: string }[]
  onCreated: (email: string, password: string) => void
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      services.admin.createUser({ email, full_name: fullName, group_ids: groupIds }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() })
      onCreated(email, result.temporary_password)
      onOpenChange(false)
      setEmail('')
      setFullName('')
      setGroupIds([])
      setError(null)
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : messages.errors.unexpected)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.users.createTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-user-email">{messages.users.email}</Label>
              <Input
                id="new-user-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-user-name">{messages.users.fullName}</Label>
              <Input
                id="new-user-name"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </div>
            <MultiSelect
              label={messages.users.groups}
              options={groupOptions}
              selected={groupIds}
              onChange={setGroupIds}
            />
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            disabled={!email || !fullName || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {messages.common.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditUserDialog({
  user,
  groupOptions,
  onClose,
}: {
  user: User | null
  groupOptions: { value: string; label: string }[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [fullName, setFullName] = useState(user?.full_name ?? '')
  const [isActive, setIsActive] = useState(user?.is_active ?? true)
  const [groupIds, setGroupIds] = useState<string[]>(user?.group_ids ?? [])

  const mutation = useMutation({
    mutationFn: () =>
      services.admin.updateUser(user!.id, {
        full_name: fullName,
        is_active: isActive,
        group_ids: groupIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() })
      onClose()
    },
  })

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.users.editTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {user && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>{messages.users.email}</Label>
                <Input value={user.email} disabled />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-user-name">{messages.users.fullName}</Label>
                <Input
                  id="edit-user-name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                />
              </div>
              <MultiSelect
                label={messages.users.groups}
                options={groupOptions}
                selected={groupIds}
                onChange={setGroupIds}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                {messages.users.isActive}
              </label>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {messages.common.cancel}
          </Button>
          <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function UsersPage() {
  const { get, set } = useQueryState()
  const page = Number(get('page') ?? '1')
  // Detay paneli URL'de: yenileyince ya da bağlantı paylaşılınca açık kalır.
  const selectedUserId = get('u') ?? null
  const queryClient = useQueryClient()

  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users({ page, page_size: PAGE_SIZE }),
    queryFn: () => services.admin.listUsers({ page, page_size: PAGE_SIZE }),
  })
  const groupsQuery = useQuery({
    queryKey: queryKeys.admin.groups(),
    queryFn: () => services.admin.listGroups(),
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null)
  // Sıfırlama açık oturumları kapatır ve geri alınamaz: önce onay.
  const [resetTarget, setResetTarget] = useState<User | null>(null)

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => services.admin.resetUserPassword(userId),
    onSuccess: (result, userId) => {
      const user = usersQuery.data?.items.find((item) => item.id === userId)
      setResetTarget(null)
      setTempPassword({ email: user?.email ?? '', password: result.temporary_password })
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (user: User) => services.admin.updateUser(user.id, { is_active: !user.is_active }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() }),
  })

  const groupOptions = (groupsQuery.data ?? []).map((group) => ({
    value: group.id,
    label: group.name,
  }))
  const groupName = (id: string) => groupsQuery.data?.find((group) => group.id === id)?.name ?? id

  const groupsText = (user: User) =>
    user.is_superadmin ? 'superadmin' : user.group_ids.map((id) => groupName(id)).join(', ')
  const activeText = (user: User) =>
    user.is_active ? messages.common.active : messages.common.inactive

  // Sunucu sıralaması yok: tüm sütunlar `enableSorting: false`. Filtre/sıralama Faz 3+ konusu değil.
  const columns: ColumnDef<User>[] = [
    {
      id: 'full_name',
      header: messages.users.fullName,
      accessorFn: (user) => user.full_name,
      enableSorting: false,
    },
    {
      id: 'email',
      header: messages.users.email,
      accessorFn: (user) => user.email,
      enableSorting: false,
    },
    {
      id: 'groups',
      header: messages.users.groups,
      accessorFn: groupsText,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex items-center gap-1">
            {user.is_superadmin ? (
              <Badge>superadmin</Badge>
            ) : user.group_ids.length === 0 ? (
              <span className="text-text-muted">{messages.users.noGroups}</span>
            ) : (
              user.group_ids.map((id) => (
                <Badge key={id} variant="secondary">
                  {groupName(id)}
                </Badge>
              ))
            )}
          </div>
        )
      },
    },
    {
      id: 'is_active',
      header: messages.users.isActive,
      accessorFn: activeText,
      enableSorting: false,
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'outline'}>
          {activeText(row.original)}
        </Badge>
      ),
    },
    {
      id: 'last_login_at',
      header: messages.users.lastLoginAt,
      accessorFn: (user) => formatDateTime(user.last_login_at),
      enableSorting: false,
    },
    {
      id: 'actions',
      header: messages.common.actions,
      enableSorting: false,
      enableHiding: false,
      size: 340,
      meta: { exportable: false },
      cell: ({ row }) => {
        const user = row.original
        return (
          // Düğmeler satır tıklamasını (detay paneli) tetiklemesin.
          <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
            <Button variant="outline" size="sm" onClick={() => setEditingUser(user)}>
              {messages.common.edit}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setResetTarget(user)}>
              {messages.users.resetPassword}
            </Button>
            {!user.is_superadmin && (
              <Button
                variant="outline"
                size="sm"
                disabled={toggleActiveMutation.isPending}
                onClick={() => toggleActiveMutation.mutate(user)}
              >
                {user.is_active ? messages.users.deactivate : messages.users.activate}
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">{messages.users.title}</h1>
          <p className="text-muted-foreground text-sm">{messages.users.description}</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus /> {messages.users.create}
        </Button>
      </div>

      {usersQuery.isLoading ? (
        <Loading rows={6} />
      ) : usersQuery.isError ? (
        <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
      ) : !usersQuery.data || usersQuery.data.items.length === 0 ? (
        <Empty />
      ) : (
        <PagedDataTable<User>
          tableId="admin-users"
          columns={columns}
          data={usersQuery.data.items}
          total={usersQuery.data.total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(next) => set({ page: String(next) })}
          onRowClick={(user) => set({ u: user.id })}
          exportFilename="kullanicilar"
        />
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        groupOptions={groupOptions}
        onCreated={(email, password) => setTempPassword({ email, password })}
      />
      <EditUserDialog
        key={editingUser?.id ?? 'none'}
        user={editingUser}
        groupOptions={groupOptions}
        onClose={() => setEditingUser(null)}
      />
      <UserDetailPanel userId={selectedUserId} onClose={() => set({ u: undefined })} />
      <ConfirmDialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setResetTarget(null)
            resetPasswordMutation.reset()
          }
        }}
        title={messages.users.resetPasswordConfirmTitle}
        description={messages.users.resetPasswordConfirmText(resetTarget?.full_name ?? '')}
        confirmLabel={messages.users.resetPassword}
        pending={resetPasswordMutation.isPending}
        error={
          resetPasswordMutation.isError
            ? resetPasswordMutation.error instanceof Error
              ? resetPasswordMutation.error.message
              : messages.errors.unexpected
            : null
        }
        onConfirm={() => resetTarget && resetPasswordMutation.mutate(resetTarget.id)}
      />
      <TempPasswordDialog info={tempPassword} onClose={() => setTempPassword(null)} />
    </div>
  )
}
